import React, { useState } from "react";
import FileTypeModal from "./FileTypeModal";
import { getFileExtension, getFileName, resolveFileUrl } from "./fileUtils";
import BIMService from "../../api/bim.service";
import EnhancedBIMViewer from "../EnhancedBIMViewer";

const BIMFileViewer = ({ file, open, onClose, projectId }) => {
  const fileName = getFileName(file);
  const extension = getFileExtension(file);
  const fileUrl = resolveFileUrl(file, projectId);
  const [converting, setConverting] = useState(false);
  const [conversionStatus, setConversionStatus] = useState(null);
  const [viewMode, setViewMode] = useState('select'); // 'select', 'convert', 'view'

  const handleConvert = async () => {
    setConverting(true);
    setConversionStatus('Starting conversion...');
    
    try {
      // Fetch the file and convert it
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const file = new File([blob], fileName, { type: blob.type });
      
      const result = await BIMService.uploadAndConvertRVT(file);
      if (result.success) {
        setConversionStatus('Conversion completed! You can now view the model.');
      } else {
        setConversionStatus(`Conversion failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setConversionStatus(`Error: ${error.message || 'Failed to convert file'}`);
    } finally {
      setConverting(false);
    }
  };

  // For IFC files, provide direct view option
  const isIFC = extension.toLowerCase() === 'ifc';

  if (viewMode === 'view' && isIFC) {
    return (
      <FileTypeModal open={open} onClose={onClose} title={fileName || "BIM File"} fullScreen>
        <EnhancedBIMViewer
          modelPath={fileUrl}
          modelName={fileName}
          paintMode={true}
          paintColor="#FF0000"
        />
        <div className="fixed bottom-4 left-4">
          <button
            onClick={() => setViewMode('select')}
            className="rounded bg-gray-600 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700"
          >
            Back to Options
          </button>
        </div>
      </FileTypeModal>
    );
  }

  return (
    <FileTypeModal open={open} onClose={onClose} title={fileName || "BIM File"}>
      <div className="space-y-4">
        {isIFC && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 font-medium">
              .IFC files can now be viewed directly in the 3D viewer.
            </p>
            <p className="text-sm text-blue-700 mt-1">
              You can also use paint workflow directly on IFC models.
            </p>
          </div>
        )}

        {!isIFC && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800 font-medium">
              .IFC requires BIM conversion for full geometry rendering in browser.
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Use converted GLB/GLTF for complete in-app view and paint workflow.
            </p>
          </div>
        )}

        {conversionStatus && (
          <div className={`p-3 rounded ${conversionStatus.includes('completed') ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-700'}`}>
            <p className="text-sm">{conversionStatus}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {isIFC && (
            <button
              onClick={() => setViewMode('view')}
              className="rounded px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
            >
              View in 3D Viewer
            </button>
          )}
          
          {!isIFC && (
            <button
              onClick={handleConvert}
              disabled={converting}
              className={`rounded px-4 py-2 text-sm font-semibold text-white ${
                converting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {converting ? 'Converting...' : 'Convert to GLB/GLTF'}
            </button>
          )}
          
          <div className="flex gap-3">
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded bg-gray-600 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700 flex-1 text-center"
            >
              Open Original
            </a>
            <a
              href={fileUrl}
              download={fileName}
              className="rounded bg-gray-700 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 flex-1 text-center"
            >
              Download
            </a>
          </div>
        </div>
      </div>
    </FileTypeModal>
  );
};

export default BIMFileViewer;
